/* eslint sort-keys: 0 */
import maxBy from "lodash/maxBy";
import {
  AdequacyAggregate,
  AdequacyDetailSpecialtyGroupsByRegion,
  AdequacyFilters,
  GapFillStatusEnum,
  GapLinkProjectModel,
  GapLinkProjectRequestModel
} from "@app/projects/network-assessment/adequacy";
import {
  AdequacyProjectResults,
  AdequacyRegionSetAggregate,
  AdequacySpecialtyGroupAggregate,
  AdequacyViewByAllComparisonRequest,
  AdequacyViewByAllComparisonResponse,
  AdequacyViewByAllRequest,
  AdequacyViewByAllResponse,
} from "@app/projects/results/adequacy";
import { AdequacyTrendChartItem, ResultRegion, } from "@app/projects/results/common";
import {
  ServicingProviderComparisonResult,
  ServicingProviderResult,
} from "@app/projects/results/servicing-provider";
import { RegionSetStatus } from "@app/region-sets";
import { FilteredResource, Resource, ResourceStatus } from "@app/resources";
import {
  ChangedGapsEnum,
  LocationConfidenceScoresLabelEnum,
  NetworkAssessmentAnalysisType,
  ProjectionProviderType,
  ProjectRunConfigurationStatus,
  ProjectRunConfigurationType,
  SpecialtyTypeEnum,
  TimeFrequency
} from "./enumerators";

import { SortDirections, WafflesFilterGroup } from "@app/shared/waffles";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { authenticatedDownloadAsync } from "@app/shared/AuthenticatedDownload";
import {
  ClaimsActivityLineOfBusinessTypeEnum,
  ClaimsActivityVolumeLabelEnum
} from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import ProviderSetCargoColumnSchema from "@app/provider-sets/providerSetCargoColumnSchema";
import { TableTypeEnum } from "@app/provider-sets/enumerations";
import {
  CareJourneyScoresValueEnum,
  EffectivenessEfficiencyValueEnum,
  MotiveScoreValueEnum,
  NewPatientClaimsEnum, SimulateProjectionProvidersModel
} from "@app/projects/servicing-provider";
import { SpecialtyPredictionModelEnum } from "@app/projects/servicing-provider/provider-claims/SpecialtyPredictionModelEnum";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { SimpleSpecialtyGroupModel } from "@app/shared/interfaces/simpleSpecialtyGroupModel";
import { AdequacyOutcomeModel } from "@app/shared/interfaces/adequacyOutcomeModel";
import { SpecialtyGroupOptionModel } from "@app/shared/interfaces/specialtyGroupOptionModel";
import { SpecialtyGroupWithSpecialtiesModel } from "@app/shared/interfaces/specialtyGroupWithSpecialtiesModel";
import UpdateProjectRunConfiguration from "@app/projects/edit/updateProjectRunConfiguration";
import { CreateProjectTagModel, ResourceTagModel } from "@app/tags/import/types";
import ProjectServiceAreaMap from "./network-assessment/maps/gap-density-map/projectServiceAreaMap";
import { ServiceAreaNestingParentDropdownItem } from "@app/report-customization/manage-presets/interfaces/serviceAreaNestingParentDropdownItem";
import { ProviderToContractEntityModel } from "@app/projects/providerToContractEntityModel";
import PublishProjectRunConfiguration from "@app/projects/edit/publishProjectRunConfiguration";
import { ReportCustomizationRunConfigurationApiResponse } from "@app/report-customization/build-reports/interfaces/reportCustomizationProjectRunConfigurationApiResponse";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import AvailableRegionStatusesModel from "@app/projects/availableRegionStatusesModel";
import { BulkProjectModel } from "@app/projects/bulkProjectModel";
import { BulkPublishModel } from "@app/projects/bulkPublishModel";
import { BulkRunSubmitResponseModel } from "@app/projects/bulkRunSubmitResponseModel";
import BulkProjectEditChange from "@app/projects/edit/bulkProjectEditChange";
import { AdequacyDetail } from "@app/projects/network-assessment/adequacy/adequacyDetail";
import CrossProjectGapsResult from "@app/projects/network-assessment/adequacy/cross-project-gaps/crossProjectGapsResult";
import { ComparedRecord } from "@app/projects/network-assessment/comparedRecord";
import Project from "@app/projects/project";
import ProjectDashboardModel from "@app/projects/projectDashboardModel";
import ProjectInfoSummary from "@app/projects/projectInfoSummary";
import ProjectOptions from "@app/projects/projectOptions";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import AdequacySummaryByRegionSet from "@app/projects/results/adequacy/adequacySummaryByRegionSet";
import AdequacySummarySpecialtyGroup from "@app/projects/results/adequacy/adequacySummarySpecialtyGroup";
import GapFillStatusHistory from "@app/projects/results/adequacy/gapFillStatusHistory";
import { ProviderResults } from "@app/projects/results/common/mapping";
import { ProviderExclusionReason } from "@app/projects/results/projection-provider";
import ProjectionProvider from "@app/projects/results/projection-provider/projectionProvider";
import ProjectionProvidersByProjectionType from "@app/projects/results/projection-provider/projectionProvidersByProjectionType";
import UpdateProjectionProvider from "@app/projects/results/projection-provider/updateProjectionProvider";
import { AdequacyTerminationSimulationDetail } from "@app/projects/servicing-provider/AdequacyTerminationSimulationDetail";
import SimulateProviderTerminationRequest from "@app/projects/servicing-provider/provider-termination/simulateProviderTerminationRequest";
import { GainsPercentEnum } from "@app/projects/servicing-provider/types";
import { BulkProviderChangeReportResponseModel } from "@app/shared/provider-change-wizards/interfaces";
import ServiceBase from "@app/shared/serviceBase";
import { querystringify } from "@app/shared/utility/querystringify";
import { GapFillStatusDescriptionMap } from "@app/projects/network-assessment/adequacy/gapFillStatusDescriptionMap";
import { CountyStatus } from "@app/projects/create/countyStatus";
import CloneProjectModel from "@app/projects/clone/cloneProjectModel";
import { ServicingProviderDetail } from "@app/projects/results/servicing-provider/servicingProviderDetail";
import { BulkProjectTypeEnum } from "@app/shared/enums/bulkProjectTypeEnum";


/** Handles communication across the wire when retrieving and interacting with projects. */
export default class ProjectService extends ServiceBase {
  /**
   * Clones a new project based on the given source project id, but with the given name and description.
   * @param project The clone project model.
   * @param runNow Indicates whether to run the project immediately after creation or not
   */
  public async cloneProject(project: CloneProjectModel, runNow: boolean): Promise<Project> {
    return await this.makePost<Project>("projects/clone?runNow=" + runNow.toString(), project);
  }

  /** Retrieves the project resources of the available projects for the currently authenticated user. */
  public async getClientProjectResources(clientId?: number): Promise<Resource[]> {
    return await this.makeGet<Resource[]>(`projects/clientProjectResources?${querystringify({
      clientId,
    })}`);
  }

  /** Retrieves the project resources of the available projects for the currently authenticated user. */
  public async getProjectResources(resourceStatus: ResourceStatus = ResourceStatus.Active, includeShared = true, includeHidden = false): Promise<Resource[]> {
    return await this.makeGet<Resource[]>(`projects/projectResources?${querystringify({
      includeHidden,
      includeShared,
      resourceStatus,
    })}`);
  }

  /**
   * Retrieves the projects of the given IDs from the API
   * @param projectIds The IDs of the projects to retrieve
   */
  public async getByIds(projectIds: number[], includeRunConfigurations = false, includeProjected = false, configurationsToLoad?: number): Promise<Project[]> {
    let endpoint = `projects/batchprojects?includeProjectRunConfigurations=${includeRunConfigurations}&includeProjected=${includeProjected}`;

    if (includeRunConfigurations && !!configurationsToLoad) {
      endpoint = `${endpoint}&configurationsToLoad=${configurationsToLoad}`;
    }

    return await this.makePost<Project[]>(endpoint, projectIds);
  }

  /**
   * Retrieves the project of the given ID from the API.
   * @param projectId The ID of the project to retrieve.
   */
  public async getById(projectId: number, includeRunConfigurations = true, includeProjected = false, configurationsToLoad?: number, filterUnpublishedRuns = false, signal?: AbortSignal): Promise<Project> {
    let endpoint = `projects/${projectId}?includeProjectRunConfigurations=${includeRunConfigurations}&includeProjected=${includeProjected}&filterUnpublishedRuns=${filterUnpublishedRuns}`;

    if (includeRunConfigurations && !!configurationsToLoad) {
      endpoint = `${endpoint}&configurationsToLoad=${configurationsToLoad}`;
    }

    return await this.makeGet<Project>(endpoint, undefined, undefined, false, undefined, signal);
  }

  public async getByIdIgnoreAuth(projectId: number, includeRunConfigurations = true, includeProjected = false, configurationsToLoad?: number): Promise<Project> {
    let endpoint = `projects/${projectId}/ignore-auth?includeProjectRunConfigurations=${includeRunConfigurations}&includeProjected=${includeProjected}`;

    if (includeRunConfigurations && !!configurationsToLoad) {
      endpoint = `${endpoint}&configurationsToLoad=${configurationsToLoad}`;
    }

    return await this.makeGet<Project>(endpoint);
  }

  /**
   * Retrieves the project of the given memberSetId ID from the API.
   * @param memberSetId The ID of the memberset to retrieve projects of.
   */
  public async getByMemberSetId(memberSetId: number): Promise<Project[]> {
    const endpoint = `projects/memberset/${memberSetId}`;

    return await this.makeGet<Project[]>(endpoint);
  }

  /**
   * Updates the region set associated with a project.
   * @param projectId The project to update.
   * @param serviceAreaRegionSetId The region set to attach.
   */
  public async updateServiceArea(projectId: number, serviceAreaRegionSetId: number): Promise<Project> {
    return await this.makePut<Project>(`projects/${projectId}/servicearea`, serviceAreaRegionSetId);
  }

  /**
   * Updates the properties of the given project
   * @param project
   */
  public async update(project: Project): Promise<Project> {
    return await this.makePut<Project>(`projects/updateproject`, project);
  }

  /**
   * Updates the properties are the given projects
   * @param projectsChange The properties to update.
   * @param projectIds The projects to update.
   */
  public async updateProjects(bulkProjectEditChange: BulkProjectEditChange): Promise<number[]> {
    return await this.makePut<number[]>(`projects/editProjects`, bulkProjectEditChange);
  }

  /**
   * Updates the date published for a project run configuration.
   * @param projectId The project to update.
   * @param publishComment The publish comment for the project to update.
   * @param runConfigurationId The run configuration associated with the desired results.
   */
  public async publishRunConfiguration(projectId: number, runConfigurationId: number, publishComment: string): Promise<void> {
    const model: PublishProjectRunConfiguration = { publishComment: publishComment };
    return await this.makePut<void>(`projects/${projectId}/runconfigurations/${runConfigurationId}/publish/`, model);
  }

  public async publishProject(projectId: number, comment: string): Promise<void> {
    return await this.makePut<void>(`projects/${projectId}/publish?comment=${encodeURIComponent(comment)}`);
  }

  public async updateRunConfiguration(projectId: number, runConfigurationId: number, model: UpdateProjectRunConfiguration): Promise<void> {
    return await this.makePatch<void>(`projects/${projectId}/runconfigurations/${runConfigurationId}/`, model);
  }

  /**
   * Triggers execution for a given project.
   * @param projectId The identifier of the project.
   */
  public async execute(projectId: number): Promise<number> {
    return await this.makePost<number>(`projects/${projectId}/submit`);
  }

  /**
   * Triggers execution for a given project.
   * @param projectId The identifier of the project.
   */
  public async executeHumana(projectId: number): Promise<number> {
    return await this.makePost<number>(`projects/${projectId}/submit-integration-user`);
  }

  /**
   * Triggers execution for a given project.
   * @param projectId The identifier of the project.
   */
  public async executeUHC(projectId: number): Promise<number> {
    return await this.makePost<number>(`projects/${projectId}/submit-uhc-guest-login`);
  }

  public async executeForClient(isHumana: boolean, isUHC: boolean, projectId: number): Promise<number> {
    if (isHumana) {
      return await this.executeHumana(projectId);
    }
    if (isUHC) {
      return await this.executeUHC(projectId);
    }
    return await this.execute(projectId);
  }

  public async cancelQueuedProject(projectId: number): Promise<void> {
    return await this.makePost(`projects/${projectId}/cancel-queued`);
  }

  /**
   * Retrieves the results for a given project and run configuration.
   * @param projectId The identifier of the project.
   * @param runConfigurationId The run configuration associated with the desired results.
   * @param networkAssessmentAnalysisType The type of analsysis for which to retreive results.
   */
  public async adequacyDetailResults(projectId: number, runConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<AdequacyProjectResults> {
    // If we don't have a projectId or runConfigurationId, return early.
    if (!projectId || !runConfigurationId || !networkAssessmentAnalysisType) {
      return null;
    }

    return await this.makeGet<AdequacyProjectResults>(
      `projects/${projectId}/runconfigurations/${runConfigurationId}/adequacydetailresults`,
      { networkAssessmentAnalysisType });
  }

  public projectTileResults(
    projectResources: FilteredResource[],
    resourceStatus: ResourceStatus = ResourceStatus.Active,
    includeShared = true,
    includeHidden = false,
  ): JQueryXHR {
    if (!projectResources || projectResources.length < 1) {
      return null;
    }

    return this.makeAbortablePost<ProjectDashboardModel>(`projects/projecttileviewresults?${querystringify({
      includeHidden,
      includeShared,
      resourceStatus,
    })}`, projectResources);
  }

  public async saveClientGaps(): Promise<void> {
    return await this.makePost("projects/saveclientgaps");
  }

  public async crossProjectGapsResult(projectResources: FilteredResource[], skip?: number, take?: number, sortDirection?: SortDirection, sortedColumn?: string): Promise<CrossProjectGapsResult> {
    if (!projectResources || projectResources.length < 1) {
      return null;
    }

    return await this.makePost<CrossProjectGapsResult>(`projects/crossprojectgapsresults?${querystringify({
      skip,
      sortDirection,
      sortedColumn,
      take,
    })}`, projectResources);
  }

  public async getGapLinkProjects(gapLinkProjectRequestModel: GapLinkProjectRequestModel): Promise<GapLinkProjectModel[]> {
    if (!gapLinkProjectRequestModel || !gapLinkProjectRequestModel.gapLinkId) {
      return null;
    }

    return await this.makePost<GapLinkProjectModel[]>(`projects/gaplinkprojects`, gapLinkProjectRequestModel);
  }

  public async getRegionSetAggregates(
    projectResources: FilteredResource[],
    resourceStatus: ResourceStatus,
    includeShared?: boolean,
    includeHidden?: boolean,
    membershipRiskOnly?: boolean,
    skip?: number,
    take?: number,
  ): Promise<AdequacyAggregate[]> {
    let optionalQuery = "";
    optionalQuery += includeShared ? `&includeShared=${includeShared}` : "";
    optionalQuery += includeHidden ? `&inclueHidden=${includeHidden}` : "";
    optionalQuery += membershipRiskOnly ? `&membershipRiskOnly=${membershipRiskOnly}` : "";
    optionalQuery += skip > 0 ? `&skip=${skip}` : "";
    optionalQuery += take < 4000 || take > 4000 ? `&take=${take}` : "";

    return await this.makePost<AdequacyAggregate[]>(`projects/regionSetAggregates?resourceStatus=${resourceStatus}${optionalQuery}`, projectResources);
  }

  public async getSpecialtyGroupAggregates(
    projectResources: FilteredResource[],
    resourceStatus: ResourceStatus,
    includeShared?: boolean,
    includeHidden?: boolean,
    skip?: number,
    take?: number,
  ): Promise<AdequacyAggregate[]> {
    let optionalQuery = "";
    optionalQuery += includeShared ? `&includeShared=${includeShared}` : "";
    optionalQuery += includeHidden ? `&inclueHidden=${includeHidden}` : "";
    optionalQuery += skip > 0 ? `&skip=${skip}` : "";
    optionalQuery += take < 4000 || take > 4000 ? `&take=${take}` : "";

    return await this.makePost<AdequacyAggregate[]>(`projects/specialtyGroupAggregates?resourceStatus=${resourceStatus}${optionalQuery}`, projectResources);
  }

  public async getSpecialtyLevelSummaries(projectId: number, projectRunConfigurationId: number, regionSetId: number, specialtyGroupCode: string): Promise<AdequacyDetail[]> {
    return await this.makeGet<AdequacyDetail[]>(`projects/${projectId}/runconfigurations/${projectRunConfigurationId}/regionsets/${regionSetId}/specialtylevelsummaries`, { specialtyGroupCode });
  }

  /**
   * Retrieves the batch service-area results for the latest run-configuration, given set of projectIds and networkAssessmentAnalysisType.
   * @param projectIds The project identifiers
   * @param networkAssessmentAnalysisType The type of analysis for which to retrieve results.
   */
  public async batchServiceAreaResults(projectIds: number[], networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<Array<{ item1: number, item2: AdequacyRegionSetAggregate }>> {
    // If we don't have projectIds or an networkAssessmentAnalysisType, return early.
    if (!projectIds || projectIds.length < 1 || !networkAssessmentAnalysisType) {
      return null;
    }

    return await this.makePost<Array<{ item1: number, item2: AdequacyRegionSetAggregate }>>(`projects/batchadequacysummaryresults?networkAssessmentAnalysisType=${networkAssessmentAnalysisType}`, projectIds);
  }

  public async getRegionalAggregates(
    projectId: number,
    runConfigurationId: number,
    regionSetIds: number[],
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType
  ): Promise<AdequacyRegionSetAggregate[]> {
    if (!projectId || !runConfigurationId || !regionSetIds?.length || !networkAssessmentAnalysisType) {
      return [];
    }

    return await this.makePost<AdequacyRegionSetAggregate[]>(
      `projects/${projectId}/runconfigurations/${runConfigurationId}/regionaladequacysummaryresults`,
      { networkAssessmentAnalysisType, regionSetIds }
    ) ?? [];
  }

  /**
   * Retrieves the results for a given project, region, and run configuration.
   * @param projectId The identifier of the project.
   * @param regionSetId The region set associated with the desired results.
   * @param runConfigurationId The run configuration associated with the desired results.
   * @param networkAssessmentAnalysisType The type of analsysis for which to retreive results.
   */
  public async regionResults(projectId: number, regionSetIds: number[], runConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<AdequacyRegionSetAggregate[]> {
    // If we don't have a projectId, regionSetId or runConfigurationId, return early.
    if (!projectId || !regionSetIds || !regionSetIds.length || !runConfigurationId || !networkAssessmentAnalysisType) {
      return null;
    }

    return await this.makePost<AdequacyRegionSetAggregate[]>(
      `projects/${projectId}/runconfigurations/${runConfigurationId}/adequacysummaryresults`,
      { networkAssessmentAnalysisType, regionSetIds });
  }

  /**
   * Retrieves the results for a given project, region, and run configuration.
   * @param projectId The identifier of the project.
   * @param regionSetId The region set associated with the desired results.
   * @param runConfigurationId The run configuration associated with the desired results.
   * @param networkAssessmentAnalysisType The type of analsysis for which to retreive results.
   */
  public async specialtyGroupResults(projectId: number, regionSetId: number, runConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<AdequacySpecialtyGroupAggregate[]> {
    // If we don't have a projectId, regionSetId or runConfigurationId, return early.
    if (!projectId || !regionSetId || !runConfigurationId || !networkAssessmentAnalysisType) {
      return null;
    }

    const res = await this.makeOptionalGet<AdequacySpecialtyGroupAggregate[]>(
      `projects/${projectId}/runconfigurations/${runConfigurationId}/regionsets/${regionSetId}/adequacydetailresults`,
      { networkAssessmentAnalysisType });

    return res || null;
  }

  /**
   * Retrieves the results for a given project, run configuration, and set of regions.
   * @param projectId The identifier of the project.
   * @param regionSetIds The region sets associated with the desired results.
   * @param runConfigurationId The run configuration associated with the desired results.
   * @param networkAssessmentAnalysisType The type of analsysis for which to retreive results.
   */
  public async specialtyGroupResultsBulk(projectId: number, specialtyGroupCode: string, regionSets: ResultRegion[], runConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<AdequacyDetailSpecialtyGroupsByRegion[]> {
    // If we don't have a projectId, regionSetId or runConfigurationId, return early.
    if (!projectId || !regionSets || !runConfigurationId || !networkAssessmentAnalysisType) {
      return null;
    }

    const ids = regionSets.map((rs) => rs.regionSetId);

    return await this.makePost<AdequacyDetailSpecialtyGroupsByRegion[]>(
      `projects/${projectId}/runconfigurations/${runConfigurationId}/regionsets/adequacydetailresults/bulk`, { networkAssessmentAnalysisType, regionSetIds: ids, specialtyGroupCode });
  }

  //public async batchedRegionDetails(projectId: number, regionSetIds: number[], runConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, testing: boolean = false): Promise<AdequacySummaryByRegionSet[]> {
  //  // Return early if we don't have any of the good stuff
  //  if (!projectId || !regionSetIds.length || !runConfigurationId || !networkAssessmentAnalysisType) {
  //    return null;
  //  }

  //  return await this.makePost<AdequacySummaryByRegionSet[]>(`projects/${projectId}/runconfigurations/${runConfigurationId}/batchedsummaries?${querystringify({ networkAssessmentAnalysisType, testing })}`, regionSetIds);
  //}

  public getAdequacyViewByAllTableModel(projectId: number, runConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, regionSetId: number, runFrequency: TimeFrequency, wafflesFilters?: WafflesFilterGroup[], sortDirection?: SortDirections, sortedColumn?: string, skip?: number, take?: number, adequacyFilters?: AdequacyFilters, isApplyingCMSCredit?: boolean, isShowingOnlyCMSCreditChanges?: boolean): JQueryXHR {
    // Return early if we don't have any of the good stuff
    if (!projectId || !runConfigurationId || !networkAssessmentAnalysisType) {
      return null;
    }

    let regionStatuses = Object.entries(adequacyFilters || []).filter((f) => ["active", "pending", "approved", "opportunity"].includes(f[0]) && f[1]).map((f) => f[0]);
    if (regionStatuses.length === 0) {
      regionStatuses = ["active", "pending", "approved"];
    }

    const requestBody: AdequacyViewByAllRequest = {
      applyCMSCredit: isApplyingCMSCredit,
      dataFiltersList: wafflesFilters,
      getExceptions: adequacyFilters ? adequacyFilters.exceptions : false,
      getGaps: adequacyFilters ? adequacyFilters.gaps : false,
      regionStatuses,
      runFrequency,
      showOnlyCMSCreditChanges: isApplyingCMSCredit && isShowingOnlyCMSCreditChanges,
      skip,
      sortDirection,
      sortedColumn,
      specialtyType: adequacyFilters?.specialtyType ?? SpecialtyTypeEnum.Unknown,
      take,
      timezoneOffset: new Date().getTimezoneOffset(),
    };
    return this.makeAbortablePost<AdequacyViewByAllResponse>(`projects/${projectId}/runconfigurations/${runConfigurationId}/regionsets/${regionSetId}/adequacyviewbyall?${querystringify({ networkAssessmentAnalysisType })}`, requestBody);
  }

  public getAdequacyViewByAllComparisonTableModel(projectId: number, runConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, regionSetId: number, compareToRunConfigurationId: number, runFrequency: TimeFrequency, wafflesFilters?: WafflesFilterGroup[], sortDirection?: SortDirections, sortedColumn?: string, skip?: number, take?: number, adequacyFilters?: AdequacyFilters): JQueryXHR {
    // Return early if we don't have any of the good stuff
    if (!projectId || !runConfigurationId || !networkAssessmentAnalysisType) {
      return null;
    }

    let regionStatuses = Object.entries(adequacyFilters || []).filter((f) => ["active", "pending", "approved", "opportunity"].includes(f[0]) && f[1]).map((f) => f[0]);
    if (regionStatuses.length === 0) {
      regionStatuses = ["active", "pending", "approved"];
    }

    const requestBody: AdequacyViewByAllComparisonRequest = {
      changedGaps: adequacyFilters?.changedGaps ?? ChangedGapsEnum.Unknown,
      compareToRunConfigurationId,
      dataFiltersList: wafflesFilters,
      getExceptions: adequacyFilters ? adequacyFilters.exceptions : false,
      getGaps: adequacyFilters ? adequacyFilters.gaps : false,
      regionStatuses,
      runFrequency,
      skip,
      sortDirection,
      sortedColumn,
      specialtyType: adequacyFilters?.specialtyType ?? SpecialtyTypeEnum.Unknown,
      take,
      timezoneOffset: new Date().getTimezoneOffset(),
    };

    return this.makeAbortablePost<AdequacyViewByAllComparisonResponse>(`projects/${projectId}/runconfigurations/${runConfigurationId}/regionsets/${regionSetId}/adequacyviewbyallcomparison?${querystringify({ networkAssessmentAnalysisType })}`, requestBody);
  }

  public async batchedComparedRegionDetails(projectId: number, regionSetIds: number[], runConfigurationId: number, compareToRunConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<Array<ComparedRecord<AdequacySummarySpecialtyGroup>>> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetIds.length || !runConfigurationId || !compareToRunConfigurationId || !networkAssessmentAnalysisType) {
      return null;
    }

    return await this.makePost<Array<ComparedRecord<AdequacySummarySpecialtyGroup>>>(`projects/${projectId}/runconfigurations/${runConfigurationId}/${compareToRunConfigurationId}/batchedcomparedsummaries?${querystringify({ networkAssessmentAnalysisType })}`, regionSetIds);
  }

  /**
   * Retrieves the adequacy detail results for a given project, region & run-configuration
   * @param projectId
   * @param regionSetId
   * @param runConfigurationId
   * @param networkAssessmentAnalysisType The type of analsysis for which to retreive results.
   */
  public async regionDetails(projectId: number, regionSetId: number, runConfigurationId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<AdequacySummaryByRegionSet[]> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !networkAssessmentAnalysisType) {
      return null;
    }

    return await this.makeGet<AdequacySummaryByRegionSet[]>(`projects/${projectId}/runconfigurations/${runConfigurationId}/regionsets/${regionSetId}/summaries`, { networkAssessmentAnalysisType });
  }

  /**
   * Retrieves the adequacy detail results for a given project, region, run-configuration & specialty-group-code
   * @param projectId
   * @param regionSetId
   * @param runConfigurationId
   * @param specialtyGroupCode
   * @param networkAssessmentAnalysisType The type of analsysis for which to retreive results.
   */
  public async regionDetailsByRegionSet(projectId: number, regionSetId: number, runConfigurationId: number, specialtyGroupCode: string, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<AdequacySummaryByRegionSet[]> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !networkAssessmentAnalysisType) {
      return null;
    }

    return await this.makeGet<AdequacySummaryByRegionSet[]>(
      `projects/${projectId}/runconfigurations/${runConfigurationId}/regionsets/${regionSetId}/summaries/byregionset`,
      { networkAssessmentAnalysisType, specialtyGroupCode });
  }

  /**
   * Retrieves the servicing providers for a given project, region, run-configuration, specialty-group-code & provider-network-type
   * @param projectId
   * @param regionSetId
   * @param runConfigurationId
   * @param specialtyGroupCode
   * @param networkAssessmentAnalysisType The type of analsysis for which to retreive results.
   * @param skip
   * @param take
   */
  public async servicingProviders(
    projectId: number,
    regionSetId: number,
    runConfigurationId: number,
    specialtyGroupCode: string,
    providerNetworkType: ProviderNetworkTypeEnum,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    gainsPercent: GainsPercentEnum,
    skip?: number,
    take?: number,
    sortedColumn?: string,
    sortDirection?: SortDirections,
    columnSubKey?: string,
    searchValue?: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    selectedNewPatientClaimsValues?: NewPatientClaimsEnum[],
    selectedTopProcedures?: string[],
    selectedCareJourneyCostValues?: CareJourneyScoresValueEnum[],
    selectedCareJourneyQualityValues?: CareJourneyScoresValueEnum[],
    selectedTopConditions?: string[],
    specialtyPredictionModelFilter?: SpecialtyPredictionModelEnum,
    selectedPgrConfidenceTiers?: number[],
  ): Promise<ServicingProviderResult> {
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !providerNetworkType || !networkAssessmentAnalysisType) {
      return null;
    }

    if (!selectedVolumeLabels) {
      selectedVolumeLabels = [];
    }

    return await this.makePost<ServicingProviderResult>(
      `projects/${projectId}/runconfigurations/${runConfigurationId}/regionsets/${regionSetId}/providers/${providerNetworkType}`,
      {
        columnSubKey,
        gainsPercent,
        networkAssessmentAnalysisType,
        searchValue,
        selectedCareJourneyCostValues,
        selectedCareJourneyQualityValues,
        selectedEffectivenessValues,
        selectedEfficiencyValues,
        selectedLineOfBusiness,
        selectedLocationConfidenceValues,
        selectedMotiveAppropriatenessValues,
        selectedNewPatientClaimsValues,
        selectedPgrConfidenceTiers,
        selectedTopConditions,
        selectedTopProcedures,
        selectedVolumeLabels,
        skip,
        sortDirection,
        sortedColumn,
        specialtyGroupCode,
        specialtyPredictionModelFilter,
        take,
      });
  }

  public async servicingAccessPoints(
    projectId: number,
    regionSetId: string,
    runConfigurationId: number,
    specialtyGroupCode: string,
    providerNetworkType: ProviderNetworkTypeEnum,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    gainsPercent: GainsPercentEnum,
  ): Promise<ProviderResults> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !providerNetworkType || !networkAssessmentAnalysisType || !gainsPercent) {
      return null;
    }

    try {
      const result = await this.makeGet<ProviderResults>(
        `projects/${projectId}/runconfigurations/${runConfigurationId}/regionsets/${regionSetId}/accesspoints/${providerNetworkType}`,
        { gainsPercent, networkAssessmentAnalysisType, specialtyGroupCode });
      return result;
    } catch {
      return null;
    }
  }

    public async servicingAccessPointsMultiRegionAsync(
    projectId: number,
    regionSetIds: string[],
    runConfigurationId: number,
    specialtyGroupCode: string,
    providerNetworkType: ProviderNetworkTypeEnum,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    gainsPercent: GainsPercentEnum,
  ): Promise<ProviderResults> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetIds || regionSetIds.length <= 0|| !runConfigurationId || !specialtyGroupCode || !providerNetworkType || !networkAssessmentAnalysisType || !gainsPercent) {
      return null;
    }

    try {
      const result = await this.makeGet<ProviderResults>(
        `projects/${projectId}/runconfigurations/${runConfigurationId}/accesspoints/${providerNetworkType}/multiregion`,
        { gainsPercent, networkAssessmentAnalysisType, specialtyGroupCode, regionSetIds });
      return result;
    } catch {
      return null;
    }
  }

  /**
   * Retrieves servicing providers for a specific project run configuration, region set, specialty group, and provider network type,
   * compared against the set of servicing providers for a different project run configuration.
   * This sucker returns early with null, if it smells something is up with the inputs. 💩
   * @param projectId The project identifier
   * @param selectedProjectRunConfigurationId The selected project run configuration ID
   * @param comparedProjectRunConfigurationId The project run configuration ID of the configuration that is being compared to the selected
   * @param regionSetId The region set identifier
   * @param specialtyGroupCode The specialty group code
   * @param providerNetworkType The provider network type
   * @param networkAssessmentAnalysisType The type of analysis
   * @param skip The skip component of retrieving results in batch
   * @param take The take component of retrieving results in batch
   * @param added Set to return providers that fall into the added group
   * @param removed Set to return providers that fall into the removed group
   * @param matched Set to return providers that fall in to the matched group
   */
  public async getServicingComparisonProviders(projectId: number, selectedProjectRunConfigurationId: number, comparedProjectRunConfigurationId: number, regionSetId: number, specialtyGroupCode: string, providerNetworkType: ProviderNetworkTypeEnum, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, added?: boolean, removed?: boolean, matched?: boolean, skip?: number, take?: number, sortedColumn?: string, sortDirection?: SortDirections): Promise<ServicingProviderComparisonResult> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !selectedProjectRunConfigurationId || !comparedProjectRunConfigurationId || !specialtyGroupCode || !providerNetworkType || !networkAssessmentAnalysisType) {
      return null;
    }

    let direction;
    if (sortDirection) {
      direction = sortDirection === SortDirections.Asc ? 3 : sortDirection === SortDirections.Desc ? 2 : 1;
    }

    return await this.makePost<ServicingProviderComparisonResult>(`projects/${projectId}/comparisonproviders`,
      { added, comparedProjectRunConfigurationId, flatten: true, matched, networkAssessmentAnalysisType, providerNetworkType, regionSetId, removed, selectedProjectRunConfigurationId, skip, sortDirection: direction, sortedColumn, specialtyGroupCode, take });
  }

  /**
   * Retrieves the servicing provider detail for a given project, region, run-configuration, specialty-group-code, provider-network-type & provider-id
   * @param projectId
   * @param regionSetId
   * @param runConfigurationId
   * @param specialtyGroupCode
   * @param providerNetworkType
   * @param providerId
   * @param networkAssessmentAnalysisType The type of analsysis for which to retreive results.
   */
  public async servicingProviderDetail(projectId: number, regionSetId: number, runConfigurationId: number, specialtyGroupCode: string, providerNetworkType: ProviderNetworkTypeEnum, providerId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, nextClosestProvider: boolean, netNewProvider: boolean): Promise<ServicingProviderDetail> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !providerNetworkType || !providerId || !networkAssessmentAnalysisType) {
      return null;
    }
    return await this.makeGet<ServicingProviderDetail>(
      `projects/${projectId}/runconfigurations/${runConfigurationId}/regionsets/${regionSetId}/providers/${providerNetworkType}/provider/${providerId}`,
      { netNewProvider, networkAssessmentAnalysisType, nextClosestProvider, specialtyGroupCode });
  }

  public async getStatuses(projectId: number, regionSetIds: number[], projectRunConfigurationId: number): Promise<RegionSetStatus[]> {
    return await this.makePost<RegionSetStatus[]>(`projects/${projectId}/statuses?projectRunConfigurationId=${projectRunConfigurationId}`, regionSetIds);
  }

  public async getAvailableStatuses(projectId: number, projectRunConfigurationId: number, signal?: AbortSignal): Promise<AvailableRegionStatusesModel[]> {
    return await this.makeGet<AvailableRegionStatusesModel[]>(`projects/${projectId}/runconfigurations/${projectRunConfigurationId}/availablestatuses`, undefined, undefined, false, undefined, signal);
  }

  public async setStatus(projectId: number, regionSetId: number, newStatus: string): Promise<RegionSetStatus> {
    return await this.makePut<RegionSetStatus>(`projects/${projectId}/statuses/${regionSetId}`, newStatus);
  }

  public async setStatuses(projectId: number, countyStatus: CountyStatus[]): Promise<RegionSetStatus> {
    const statusMap = countyStatus.map((cs) => {
      return {
        regionSetId: cs.regionSetId,
        regionSetStatus: cs.status,
      };
    });
    return await this.makePut<RegionSetStatus>(`projects/${projectId}/statuses`, statusMap);
  }

  public async deleteStatus(projectId: number, regionSetId: number, projectRunConfigurationId: number): Promise<void> {
    return await this.makeDelete(`projects/${projectId}/statuses/${regionSetId}?projectRunConfigurationId=${projectRunConfigurationId}`);
  }

  public async getCountyStatusInfo(customProjectId: number, regionSetId: number, projectRunConfigurationId?: number): Promise<CountyStatus[]> {
    return await this.makeGet<CountyStatus[]>(`projects/${customProjectId}/regionstatusinfo/${regionSetId}`, projectRunConfigurationId);
  }

  public async createProjectionProviders(
    projectId: number,
    providerNetworkType: ProviderNetworkTypeEnum,
    projectionType: ProjectionProviderType,
    projectedByNPI: boolean,
    projectedByTIN: boolean,
    projectedByContractEntity: boolean,
    projectedByNPI2: boolean,
    providerIds: number[],
    reasonName: string,
    reasonDescription: string,
    allSpecialties: boolean,
    allLocations: boolean,
    allProjects: boolean,
    phone: string,
    specialtyGroupCode: string,
    exclusionVerifiedSources: string[],
    providerIdToContractEntityId: ProviderToContractEntityModel[],
    projectIds: number[],
    runAdvancedExclusionsProjects: boolean,
    projectedByCCN = false,
    ccn = ""
  ): Promise<ProjectionProvider[]> {
    return await this.makePost<ProjectionProvider[]>(`projects/${projectId}/projectionproviders`, { allLocations, allProjects, allSpecialties, ccn, exclusionVerifiedSources, phone, projectedByCCN, projectedByContractEntity, projectedByNPI, projectedByNPI2, projectedByTIN, projectIds, projectionType, providerIds, providerIdToContractEntityId, providerNetworkType, reasonDescription, reasonName, runAdvancedExclusionsProjects, specialtyGroupCode });
  }

  public async deleteProjectionProviders(projectId: number, projectionProviderProjectIds: number[]): Promise<void> {
    return await this.makeDelete(`projects/${projectId}/projectionproviders`, projectionProviderProjectIds);
  }

  public async updateProjectionProvider(projectId: number, updateProjectionProvider: UpdateProjectionProvider): Promise<void> {
    return await this.makePatch(`projects/${projectId}/projectionproviders`, updateProjectionProvider);
  }

  public async getTargetProviderReasons(projectId: number): Promise<string[]> {
    return await this.makeGet(`projects/${projectId}/providertargetreasons`);
  }

  public async getProviderExclusionReasons(projectId: number): Promise<ProviderExclusionReason[]> {
    return await this.makeGet(`projects/${projectId}/providerexclusionreasons`);
  }

  public async restoreProjectionProviders(projectId: number, projectionProviderProjectIds: number[]): Promise<void> {
    return await this.makePost(`projects/${projectId}/restoreprojectionproviders`, projectionProviderProjectIds);
  }

  /**
   * Retrieves the impact-projection-provider detail for a given project, region, run-configuration, specialty-group-code & provider-network-type
   * @param projectId
   * @param regionSetId
   * @param runConfigurationId
   * @param specialtyGroupCode
   * @param providerNetworkType
   * @param networkAssessmentAnalysisType The type of analsysis for which to retreive results.
   */
  public async getImpactProjectionProviders(
    projectId: number,
    regionSetId: number,
    runConfigurationId: number,
    specialtyGroupCode: string,
    providerNetworkType: ProviderNetworkTypeEnum,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    gainsPercent: GainsPercentEnum,
    sortedColumn: string,
    sortDirection: SortDirections,
    columnSubKey: string,
    searchValue?: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    selectedNewPatientClaimsValues?: NewPatientClaimsEnum[],
    selectedTopProcedures?: string[],
    selectedCareJourneyCostValues?: CareJourneyScoresValueEnum[],
    selectedCareJourneyQualityValues?: CareJourneyScoresValueEnum[],
    selectedTopConditions?: string[],
    specialtyPredictionModelFilter?: SpecialtyPredictionModelEnum,
    selectedPgrConfidenceTiers?: number[],
  ): Promise<ProjectionProvidersByProjectionType[]> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !providerNetworkType || !networkAssessmentAnalysisType) {
      return null;
    }

    if (!selectedVolumeLabels) {
      selectedVolumeLabels = [];
    }

    return await this.makePost<ProjectionProvidersByProjectionType[]>(
      `projects/${projectId}/runconfigurations/${runConfigurationId}/regionsets/${regionSetId}/projectionproviders/${providerNetworkType}`,
      {
        columnSubKey,
        gainsPercent,
        networkAssessmentAnalysisType,
        searchValue,
        selectedCareJourneyCostValues,
        selectedCareJourneyQualityValues,
        selectedEffectivenessValues,
        selectedEfficiencyValues,
        selectedLineOfBusiness,
        selectedLocationConfidenceValues,
        selectedMotiveAppropriatenessValues,
        selectedNewPatientClaimsValues,
        selectedPgrConfidenceTiers,
        selectedTopConditions,
        selectedTopProcedures,
        selectedVolumeLabels,
        sortDirection,
        sortedColumn,
        specialtyGroupCode,
        specialtyPredictionModelFilter,
      });
  }

  public async getUnprocessedProjectionProvider(projectId: number, projectionType?: ProjectionProviderType): Promise<ProjectionProvider[]> {
    if (!projectId) {
      return null;
    }

    return await this.makeGet<ProjectionProvider[]>(`projects/${projectId}/getunprocessedprojectionproviders?${querystringify({ projectionType })}`);
  }

  public async getProcessedProjectionProvider(projectId: number, projectionType?: ProjectionProviderType): Promise<ProjectionProvider[]> {
    if (!projectId) {
      return null;
    }
    return await this.makeGet<ProjectionProvider[]>(`projects/${projectId}/getprocessedprojectionproviders?${querystringify({ projectionType })}`);
  }

  public async processProviderChanges(projectId: number, reprocessFailure: boolean, shouldSkipTerminationTimeDistanceRecalc?: boolean): Promise<void> {
    // Return early if we don't have any of the good stuff
    if (!projectId) {
      return null;
    }
    return await this.makePost<void>(`projects/${projectId}/processproviderchanges?${querystringify({ reprocessFailure, shouldSkipTerminationTimeDistanceRecalc: shouldSkipTerminationTimeDistanceRecalc })}`);
  }

  private isInvalidProjectRunConfigurationForSharedAndHiddenResources(rc: ProjectRunConfiguration): boolean {
    return (
      rc.isProjected || rc.isTermed || rc.isProjectedTermed || !rc.datePublished ||
      [
        ProjectRunConfigurationStatus.QueuedProviderChanges,
        ProjectRunConfigurationStatus.ProcessingProviderChanges,
        ProjectRunConfigurationStatus.QueuedSpecialtyGroupExceptionAnalysis,
        ProjectRunConfigurationStatus.ProcessingSpecialtyGroupExceptionAnalysis,
        ProjectRunConfigurationStatus.Succeeded,
        ProjectRunConfigurationStatus.FailedProviderChanges,
        ProjectRunConfigurationStatus.FailedProspectMarketNcp,
        ProjectRunConfigurationStatus.QueuedProspectMarketNcp,
        ProjectRunConfigurationStatus.ProcessingProspectMarketNcp
      ].indexOf(rc.status) < 0
    );
  }

  private isInvalidProjectRunConfiguration(rc: ProjectRunConfiguration): boolean {
    return (
      rc.isProjected || rc.isTermed || rc.isProjectedTermed || rc.status === ProjectRunConfigurationStatus.Canceled
    );
  }

  public maxRunConfigurationId(project: Project | ProjectOptions, dualMembershipRunConfigurationType: ProjectRunConfigurationType): number {
    const filteredRunConfigurations = project.projectRunConfigurations && project.projectRunConfigurations.filter((rc) =>
      [dualMembershipRunConfigurationType, ProjectRunConfigurationType.Standard].includes(rc.projectRunConfigurationType));

    if (!filteredRunConfigurations || filteredRunConfigurations.length <= 0) {
      return null;
    }

    if (project.resource.isShared || project.resource.isHidden) {
      return maxBy(filteredRunConfigurations, (rc: ProjectRunConfiguration) =>
        this.isInvalidProjectRunConfigurationForSharedAndHiddenResources(rc) ? 0 : rc.projectRunConfigurationId).projectRunConfigurationId;
    }

    return maxBy(filteredRunConfigurations, (rc) =>
      this.isInvalidProjectRunConfiguration(rc) ? 0 : rc.projectRunConfigurationId).projectRunConfigurationId;
  }

  public maxLastPublishedRunConfigurationId(project: Project | ProjectOptions, dualMembershipRunConfigurationType: ProjectRunConfigurationType): number {
    const filteredRunConfigurations = project.projectRunConfigurations && project.projectRunConfigurations.filter((rc) =>
      [dualMembershipRunConfigurationType, ProjectRunConfigurationType.Standard].includes(rc.projectRunConfigurationType));

    if (!filteredRunConfigurations || filteredRunConfigurations.length <= 0) {
      return null;
    }

    const latestPublished: ProjectRunConfiguration = filteredRunConfigurations.find((rc) =>
      rc.isLatestPublished && !rc.isProjected && !rc.isTermed && !rc.isProjectedTermed && rc.status !== ProjectRunConfigurationStatus.Canceled);

    if (!latestPublished) {
      return this.maxRunConfigurationId(project, dualMembershipRunConfigurationType);
    }

    return latestPublished.projectRunConfigurationId;
  }

  public async simulateProjection(projectId: number, regionSetId: number, specialtyGroupCode: string, requestBody: SimulateProjectionProvidersModel, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, isProjectedTermed: boolean, isTermed: boolean, dualMembershipRunConfigurationType: ProjectRunConfigurationType): Promise<AdequacyDetail> {
    if (!projectId || !regionSetId || !specialtyGroupCode || !requestBody.providerNetworkType || !requestBody.projectionType || !requestBody.providerIds) {
      return null;
    }

    return await this.makePost<AdequacyDetail>(`projects/${projectId}/regionsets/${regionSetId}/simulateprojection?${querystringify({
      dualMembershipRunConfigurationType,
      isProjectedTermed,
      isTermed,
      networkAssessmentAnalysisType,
      specialtyGroupCode
    })}`,
      requestBody
    );
  }

  public async simulateTermination(
    projectId: number,
    regionSetId: number,
    requestBody: SimulateProviderTerminationRequest,
    specialtyGroupCode: string,
    dualMembershipRunConfigurationType: ProjectRunConfigurationType,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
  ): Promise<AdequacyTerminationSimulationDetail> {
    if (!projectId || !regionSetId || !requestBody || !requestBody.providerNetworkType || !requestBody.providerIds || !specialtyGroupCode) {
      return null;
    }

    const simulatedTermination = await this.makePost<AdequacyTerminationSimulationDetail>(
      `projects/${projectId}/regionsets/${regionSetId}/simulatetermination?${querystringify(
        { dualMembershipRunConfigurationType, networkAssessmentAnalysisType, specialtyGroupCode })}`,
      requestBody,
    );

    return simulatedTermination;
  }

  /* ****************
   * Trend Chart Data
   * **************** */

  public async getTrendChartData(projectId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, runFrequency: TimeFrequency, dualMembershipProjectRunConfigurationType: ProjectRunConfigurationType): Promise<AdequacyTrendChartItem[]> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !networkAssessmentAnalysisType || !runFrequency) {
      return null;
    }
    return await this.makeGet<AdequacyTrendChartItem[]>(`projects/${projectId}/trendgraphdata/${runFrequency}`, { dualMembershipProjectRunConfigurationType, networkAssessmentAnalysisType });
  }

  public async getTrendChartDataByRegionSet(projectId: number, regionSetId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, runFrequency: TimeFrequency, dualMembershipProjectRunConfigurationType: ProjectRunConfigurationType): Promise<AdequacyTrendChartItem[]> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !networkAssessmentAnalysisType || !runFrequency) {
      return null;
    }
    return await this.makeGet<AdequacyTrendChartItem[]>(`projects/${projectId}/regionsets/${regionSetId}/trendgraphdata/${runFrequency}`, { dualMembershipProjectRunConfigurationType, networkAssessmentAnalysisType });
  }

  public async getTrendChartDataRegionSetSpecialtyGroup(projectId: number, regionSetId: number, specialtyGroupCode: string, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, runFrequency: TimeFrequency, dualMembershipProjectRunConfigurationType: ProjectRunConfigurationType): Promise<AdequacyTrendChartItem[]> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !specialtyGroupCode || !networkAssessmentAnalysisType || !runFrequency) {
      return null;
    }
    return await this.makeGet<AdequacyTrendChartItem[]>(`projects/${projectId}/regionsets/${regionSetId}/trendgraphdatabyregionsetspecialtygroup/${runFrequency}`, { dualMembershipProjectRunConfigurationType, networkAssessmentAnalysisType, specialtyGroupCode });
  }

  public async bulkRun(projectIds: number[]): Promise<BulkRunSubmitResponseModel> {
    if (projectIds.length > 0) {
      return await this.makePost<BulkRunSubmitResponseModel>("projects/submit", { ProjectIds: projectIds });
    }
    return {
      submitResults: [],
    };
  }

  public async bulkPublish(projectIds: number[], comment: string): Promise<number[]> {
    if (projectIds.length > 0) {
      return await this.makePost<number[]>("projects/publish", { ProjectIds: projectIds, PublishComment: comment });
    }
    return [];
  }

  public async getRunnableProjects(resourceIds: number[]): Promise<BulkProjectModel[]> {
    return await this.makePost<BulkProjectModel[]>("projects/runnable", resourceIds);
  }

  public async getEditableProjects(projectType?: BulkProjectTypeEnum): Promise<BulkProjectModel[]> {
    return await this.makeGet<BulkProjectModel[]>(
      projectType === undefined ? "projects/editable" : `projects/editable?projectType=${projectType}`,
    );
  }

  public async getPublishableProjects(resourceIds: number[]): Promise<BulkPublishModel[]> {
    return await this.makePost<BulkPublishModel[]>("projects/publishable", resourceIds);
  }

  public async getArchivableProjects(resourceIds: number[]): Promise<BulkProjectModel[]> {
    return await this.makePost<BulkProjectModel[]>("projects/archivable", resourceIds);
  }

  public async projectHasImpactProviders(projectId: number, regionSetId: number, runConfigurationId: number, specialtyGroupCode: string, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType): Promise<boolean> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !networkAssessmentAnalysisType) {
      return null;
    }
    //[HttpGet("{customProjectId}/runconfigurations/{projectRunConfigurationId}/regionsets/{regionSetId}/providers/hasImpact/{providerNetworkType}")]
    return await this.makeGet<boolean>(
      `projects/${projectId}/runconfigurations/${runConfigurationId}/regionsets/${regionSetId}/providers/hasImpact`,
      { networkAssessmentAnalysisType, specialtyGroupCode });
  }

  public async getBulkProviderTargetExports(): Promise<BulkProviderChangeReportResponseModel[]> {
    return await this.makeGet<BulkProviderChangeReportResponseModel[]>('projectionProviders/bulkTargetExports');
  }

  public async resubmitProjectJob(customProjectId: number, runConfigurationId: number, jobId: number, overrideJobQueueName: string, isHungJob = false): Promise<number> {
    return await this.makePost<number>(`projects/${customProjectId}/runconfigurations/${runConfigurationId}/resubmit/${jobId}?${querystringify({ isHungJob, overrideJobQueueName })}`);
  }

  public async resubmitHumanaProjectJob(customProjectId: number, runConfigurationId: number, jobId: number, overrideJobQueueName: string, isHungJob = false): Promise<number> {
    return await this.makePost<number>(`projects/${customProjectId}/runconfigurations/${runConfigurationId}/resubmit-integration-user/${jobId}?${querystringify({ isHungJob, overrideJobQueueName })}`);
  }

  public async resubmitUHCProjectJob(customProjectId: number, runConfigurationId: number, jobId: number, overrideJobQueueName: string, isHungJob = false): Promise<number> {
    return await this.makePost<number>(`projects/${customProjectId}/runconfigurations/${runConfigurationId}/resubmit-uhc-guest-login/${jobId}?${querystringify({ isHungJob, overrideJobQueueName })}`);
  }

  public async resubmitProjectJobForClient(
    isHumana: boolean,
    isUHC: boolean,
    customProjectId: number,
    runConfigurationId: number,
    jobId: number,
    overrideJobQueueName: string,
    isHungJob = false,
  ): Promise<number> {
    if (isHumana) {
      return await this.resubmitHumanaProjectJob(customProjectId, runConfigurationId, jobId, overrideJobQueueName, isHungJob);
    }
    if (isUHC) {
      return await this.resubmitUHCProjectJob(customProjectId, runConfigurationId, jobId, overrideJobQueueName, isHungJob);
    }
    return await this.resubmitProjectJob(customProjectId, runConfigurationId, jobId, overrideJobQueueName, isHungJob);
  }

  public async getProjectRunConfigurationByJobAsync(jobId: number): Promise<ProjectRunConfiguration> {
    return await this.makeGet<ProjectRunConfiguration>(`projects/job/${jobId}/runconfiguration`);
  }

  public async getProjectSummaryByProviderSetIdAsync(providerSetId: number): Promise<ProjectInfoSummary[]> {
    return await this.makeGet<ProjectInfoSummary[]>(`projects/summary/providerset/${providerSetId}`);
  }

  public async getV1ProjectByProviderSetIdAsync(providerSetId: number, providerSetType: string): Promise<ProjectInfoSummary[]> {
    return await this.makeGet<ProjectInfoSummary[]>(`projects/v1/summary/providerset/${providerSetId}/${providerSetType}`);
  }

  public async getProjectDetailsCsv(fileName?: string, clientId?: number): Promise<void> {
    let urlSearchParams = new URLSearchParams();

    if (!!clientId) {
      const query = {
        clientId: !clientId ? null : clientId.toString()
      };
      urlSearchParams = new URLSearchParams(query)
    }

    if (!!fileName && fileName.split('.').pop().toLowerCase() !== `csv`) {
      fileName += `.csv`;
    }

    return await authenticatedDownloadAsync(`${window.apiEndpointV2_0}/projects/project-config/csv`, null, fileName, urlSearchParams);
  }

  public async getProjectCountyStatusesCsv(fileName?: string, clientId?: number): Promise<void> {
    let urlSearchParams = new URLSearchParams();

    const query = {
      allowCrossClient: !clientId ? true.toString() : false.toString(),
      clientId: !clientId ? null : clientId.toString(),
    };
    urlSearchParams = new URLSearchParams(querystringify(query))

    if (!!fileName && fileName.split('.').pop().toLowerCase() !== `csv`) {
      fileName += `.csv`;
    }

    return await authenticatedDownloadAsync(`${window.apiEndpointV2_0}/projects/project-county-status/csv`, null, fileName, urlSearchParams);
  }

  public async getProjectServiceAreaMapAsync(projectId: number, runConfigurationId: number, isProjected: boolean, isTermed: boolean): Promise<ProjectServiceAreaMap[]> {
    return await this.makeGet<ProjectServiceAreaMap[]>(`projects/${projectId}/runconfigurations/${runConfigurationId}/servicearea`, { isProjected, isTermed });
  }

  public async getProviderSetCargoSchemata(customProjectId: number, runConfigurationId: number, providerNetworkType: ProviderNetworkTypeEnum, tableType: TableTypeEnum): Promise<ProviderSetCargoColumnSchema[]> {
    const schemata = await this.makeGet<ProviderSetCargoColumnSchema[]>(`projects/${customProjectId}/runconfigurations/${runConfigurationId}/providers/${providerNetworkType}/cargo-schemata?tableType=${tableType}`);
    return schemata;
  }

  public async getGapFillStatusDescriptions(): Promise<GapFillStatusDescriptionMap> {
    return await this.makeGet<GapFillStatusDescriptionMap>(`projects/gap-fill-status-descriptions`);
  }

  public async getGapFillStatusHistory(customProjectId: number, projectRunConfigurationId: number, regionSetId: number, specialtyGroupCode: string): Promise<GapFillStatusHistory[]> {
    return await this.makeGet<GapFillStatusHistory[]>(`projects/${customProjectId}/run-configurations/${projectRunConfigurationId}/region-sets/${regionSetId}/specialty-group-codes/${specialtyGroupCode}/gap-fill-status-history`);
  }

  public async updateGapFillStatusWithRegionSetAsync(customProjectId: number, projectRunConfigurationId: number, regionSetId: number, specialtyGroupCode: string, gapFillStatus: GapFillStatusEnum, comment: string): Promise<void> {
    return await this.makePut<void>(`projects/${customProjectId}/run-configurations/${projectRunConfigurationId}/region-sets/${regionSetId}/specialty-group-codes/${specialtyGroupCode}/gap-fill-statuses/${gapFillStatus}`, comment);
  }

  public async updateGapFillStatusAsync(customProjectId: number, projectRunConfigurationId: number, regionSetSpecialtyGroupId: number, gapFillStatus: GapFillStatusEnum, comment: string): Promise<void> {
    return await this.makePut<void>(`projects/${customProjectId}/run-configurations/${projectRunConfigurationId}/region-set-specialty-groups/${regionSetSpecialtyGroupId}/gap-fill-statuses/${gapFillStatus}`, comment);
  }

  public async getTimeslicesByProjectId(projectId: string): Promise<ReportCustomizationRunConfigurationApiResponse> {
    return await this.makeGet(`projects/${projectId}/timeslices`);
  }

  public async getCountiesByRunConfigurationId(projectId: string, runConfigurationId: string, useCanonicalRegionsSets: boolean = true, displayStateName: boolean = false, signal?: AbortSignal): Promise<ServiceAreaNestingParentDropdownItem[]> {
    return await this.makeGet(`projects/${projectId}/runconfigurations/${runConfigurationId}/serviceareas/counties?${querystringify({ displayStateName, useCanonicalRegionsSets })}`, undefined, undefined, false, undefined, signal);
  }

  public async getStatesByRunConfigurationId(projectId: string, runConfigurationId: string): Promise<DropdownItem<string>[]> {
    return await this.makeGet(`projects/${projectId}/runconfigurations/${runConfigurationId}/serviceareas/states`);
  }

  public async getSpecialtiesByRunConfigurationId(projectId: string, runConfigurationId: string): Promise<DropdownItem<string>[]> {
    const simpleSpecialtyModels = await this.makeGet<SimpleSpecialtyGroupModel[]>(`projects/${projectId}/runconfigurations/${runConfigurationId}/specialtygroups`);
    // The server returns groups already sorted by template display order
    // (category -> group -> name), so preserve that order via the array index.
    return simpleSpecialtyModels.map((s, index) => ({
      key: s.code,
      name: `${s.code} - ${s.name}`,
      order: index,
    }));
  }

  /** Server-sorted by template display order (category -> group -> name); preserve the array order. */
  public async getAdequacySpecialtyGroupsByRunConfigurationId(projectId: string, runConfigurationId: string, signal?: AbortSignal): Promise<SpecialtyGroupOptionModel[]> {
    return await this.makeGet<SpecialtyGroupOptionModel[]>(`projects/${projectId}/runconfigurations/${runConfigurationId}/adequacy-specialty-groups`, undefined, undefined, false, undefined, signal);
  }

  /** One row per county/specialty-group combination that was analysed, with its access and servicing results. */
  public async getAdequacyOutcomesByRunConfigurationId(projectId: string, runConfigurationId: string, signal?: AbortSignal): Promise<AdequacyOutcomeModel[]> {
    return await this.makeGet<AdequacyOutcomeModel[]>(`projects/${projectId}/runconfigurations/${runConfigurationId}/adequacy-outcomes`, undefined, undefined, false, undefined, signal);
  }

  public async getSpecialtyGroupsWithSpecialtiesByRunConfigurationId(projectId: string, runConfigurationId: string): Promise<SpecialtyGroupWithSpecialtiesModel[]> {
    return await this.makeGet<SpecialtyGroupWithSpecialtiesModel[]>(`projects/${projectId}/runconfigurations/${runConfigurationId}/specialty-groups-with-specialties`);
  }

  public async deleteGapFillStatusHistoryAsync(customProjectId: number, regionSetId: number, specialtyGroupCode: string): Promise<void> {
    return await this.makeDelete<void>(`projects/${customProjectId}/region-sets/${regionSetId}/specialty-group-codes/${specialtyGroupCode}/gap-fill-status`);
  }

  public async deleteGapFillStatusItemAsync(customProjectId: number, regionSetId: number, specialtyGroupCode: string, gapFillStatus: GapFillStatusEnum, gapFillStatusUpdatedDate: string): Promise<void> {
    return await this.makeDelete<void>(`projects/${customProjectId}/region-sets/${regionSetId}/specialty-group-codes/${specialtyGroupCode}/gap-fill-status/${gapFillStatus}?gapFillStatusUpdatedDate=${gapFillStatusUpdatedDate}`);
  }

  public async createProjectTagsAsync(tagModels: CreateProjectTagModel[]): Promise<ResourceTagModel[]> {
    return await this.makePost<ResourceTagModel[]>("projects/tags", tagModels);
  }
}